import React, { useState } from 'react';
import {
    Button,
    Form,
    Label,
    Input,
    Row,
    FormText,
    ModalHeader,
    ModalBody,
    ModalFooter,
} from 'reactstrap';
import DraggableModal from 'components/common/DraggableModal';
import BlockUi from 'react-block-ui';
import { ScaleLoader as Loader } from 'react-spinners';
import uniqueId from 'lodash/uniqueId';
import { PasswordResetModal } from 'store/accountMgmt/types';

interface ResetPasswordModalProps {
    header: string;
    open: boolean;
    locked: boolean;
    source: PasswordResetModal['source'];
    actions: {
        close: () => void;
        submit: () => void;
        changeState: (update: { [key: string]: string }) => void;
    };
}

const ChangePasswordModal: React.FC<ResetPasswordModalProps> = ({ header, open, locked, source, actions }) => {
    const { userId, currentUser, password, confirmPassword, currentPassword } = source;
    const { close, submit, changeState } = actions;

    const [errors, setErrors] = useState({
        password: '',
        confirmPassword: '',
        currentPassword: '',
    });

    const validate = () => {
        const newErrors = {
            password: '',
            confirmPassword: '',
            currentPassword: '',
        };

        if (currentUser?.userId === userId && !currentPassword) {
            newErrors.currentPassword = 'Current password is required';
        }

        if (!password) {
            newErrors.password = 'Password is required';
        }

        if (!confirmPassword) {
            newErrors.confirmPassword = 'Confirm your password';
        } else if (confirmPassword !== password) {
            newErrors.confirmPassword = 'Passwords mismatch.';
        }

        setErrors(newErrors);
        return !Object.values(newErrors).some(Boolean);
    };

    const handleSubmit = () => {
        if (validate()) {
            submit();
        }
    };

    const onClose = () => {
        setErrors({ password: '', confirmPassword: '', currentPassword: '' });
        close();
    };

    const onEdit = (key: string, value: string) => changeState({ [key]: value });

    const draggableId = `draggable-${uniqueId()}`;

    return (
        <DraggableModal isOpen={open} toggle={onClose} centered handle={`#${draggableId}`} backdrop="static">
            <BlockUi tag="div" blocking={locked} loader={<Loader color={'var(--first)'} loading={locked} />}>
                <ModalHeader id={draggableId} toggle={onClose} className='move-cursor'>{header}</ModalHeader>
                <ModalBody className='no-cursor px-5'>
                    <Form>
                        {currentUser?.userId === userId && (
                            <Row className="my-2">
                                <Label for="reset-current-password">Current password</Label>
                                <Input
                                    id="reset-current-password"
                                    placeholder="Current password"
                                    type="password"
                                    value={currentPassword || ''}
                                    onChange={(e) => onEdit('currentPassword', e.target.value)}
                                />
                                {errors.currentPassword && <FormText color="danger">{errors.currentPassword}</FormText>}
                            </Row>
                        )}
                        <Row className="my-2">
                            <Label for="reset-password">Password</Label>
                            <Input
                                id="reset-password"
                                placeholder="Password"
                                type="password"
                                value={password || ''}
                                onChange={(e) => onEdit('password', e.target.value)}
                            />
                            {errors.password && <FormText color="danger">{errors.password}</FormText>}
                        </Row>
                        <Row className="my-2">
                            <Label for="reset-confirm-password">Confirm password</Label>
                            <Input
                                id="reset-confirm-password"
                                placeholder="Confirm password"
                                type="password"
                                value={confirmPassword || ''}
                                onChange={(e) => onEdit('confirmPassword', e.target.value)}
                            />
                            {errors.confirmPassword && <FormText color="danger">{errors.confirmPassword}</FormText>}
                        </Row>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="link" onClick={onClose}>Close</Button>
                    <Button color="primary" className="btn-link-dark" onClick={handleSubmit}>Reset</Button>
                </ModalFooter>
            </BlockUi>
        </DraggableModal>
    );
};

export default ChangePasswordModal;